import React from "react";
import {
    AboutContactSettings,
    distributeGroupsToColumns,
    getPanelColumnCount,
    isAboutPanel,
    linkLooksLikeCta,
    MenuLink,
    splitPanelItems,
} from "./menuUtils";
import { AboutContactBlock } from "./AboutContactBlock";

type Props = {
    panel?: MenuLink;
    aboutContact: AboutContactSettings;
    layout: "desktop" | "mobile";
};

function MenuAnchor({ link, className = "" }: { link: MenuLink; className?: string }) {
    const isCta = linkLooksLikeCta(link.title);
    return (
        <a
            href={link.url || "#"}
            className={`side-header-menu__link ${isCta ? "side-header-menu__link--cta" : ""} ${className}`}
        >
            {link.title}
        </a>
    );
}

function GroupHeading({ group, layout }: { group: MenuLink; layout: "desktop" | "mobile" }) {
    const className =
        layout === "mobile"
            ? "side-header-menu__group-heading side-header-menu__group-heading--mobile"
            : "side-header-menu__group-heading";

    return (
        <a href={group.url || "#"} className={className}>
            {group.title}
        </a>
    );
}

function GroupBlock({ group, layout }: { group: MenuLink; layout: "desktop" | "mobile" }) {
    return (
        <div className="side-header-menu__group">
            <GroupHeading group={group} layout={layout} />
            <div className="side-header-menu__group-links">
                {(group.links || []).map((link) => (
                    <MenuAnchor
                        key={link.handle || link.title}
                        link={link}
                        className={layout === "mobile" ? "side-header-menu__link--mobile" : ""}
                    />
                ))}
            </div>
        </div>
    );
}

export function PanelContent({ panel, aboutContact, layout }: Props) {
    if (!panel) return null;

    const { groups, standalone } = splitPanelItems(panel);
    const showAboutContact = isAboutPanel(panel);

    if (layout === "mobile") {
        return (
            <div className="side-header-menu__panel-mobile">
                {standalone.map((link) => (
                    <MenuAnchor
                        key={link.handle || link.title}
                        link={link}
                        className="side-header-menu__link--mobile"
                    />
                ))}
                {groups.map((group) => (
                    <GroupBlock key={group.handle || group.title} group={group} layout="mobile" />
                ))}
                {showAboutContact && <AboutContactBlock contact={aboutContact} />}
            </div>
        );
    }

    if (showAboutContact) {
        const aboutLinks = [
            ...standalone,
            ...groups.flatMap((group) => group.links || []),
        ];

        return (
            <div className="side-header-menu__columns">
                {aboutLinks.length > 0 && (
                    <div className="side-header-menu__column">
                        <div className="side-header-menu__group-links">
                            {aboutLinks.map((link) => (
                                <MenuAnchor key={link.handle || link.title} link={link} />
                            ))}
                        </div>
                    </div>
                )}
                <div className="side-header-menu__column">
                    <AboutContactBlock contact={aboutContact} />
                </div>
            </div>
        );
    }

    const columnCount = getPanelColumnCount(groups, standalone.length);
    const columns = distributeGroupsToColumns(groups, columnCount);
    const stackStandalonesInFirstColumn =
        standalone.length > 0 && columnCount >= 2;

    return (
        <div className="side-header-menu__panel-desktop">
            {standalone.length > 0 && !stackStandalonesInFirstColumn && (
                <div className="side-header-menu__standalone-row">
                    {standalone.map((link) => (
                        <MenuAnchor key={link.handle || link.title} link={link} />
                    ))}
                </div>
            )}
            <div className="side-header-menu__columns">
                {columns.map((columnGroups, columnIndex) => (
                    <div key={`col-${columnIndex}`} className="side-header-menu__column">
                        {stackStandalonesInFirstColumn && columnIndex === 0 && (
                            <div className="side-header-menu__standalone-stack">
                                {standalone.map((link) => (
                                    <MenuAnchor
                                        key={link.handle || link.title}
                                        link={link}
                                        className="side-header-menu__link--standalone"
                                    />
                                ))}
                            </div>
                        )}
                        {columnGroups.map((group) => (
                            <GroupBlock
                                key={group.handle || group.title}
                                group={group}
                                layout="desktop"
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}
